// fcmService.js - Firebase Cloud Messaging token management & foreground messages
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getApp } from 'firebase/app';
import { doc, setDoc } from 'firebase/firestore';
import { getFirebase } from '@services/firebase';
import { Capacitor } from '@capacitor/core';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

let messagingInstance = null;

const getMessagingInstance = () => {
  if (!messagingInstance) {
    const app = getApp();
    messagingInstance = getMessaging(app);
  }
  return messagingInstance;
};

const saveTokenToFirestore = async (token, userId, userRole) => {
  const { db } = getFirebase();
  await setDoc(doc(db, 'fcmTokens', userId), {
    token,
    role: userRole || 'User',
    updatedAt: new Date().toISOString(),
  });
};

/**
 * Register for FCM push and save the device token to Firestore.
 * Handles both native (Capacitor) and web (PWA) contexts.
 */
export const registerFCMToken = async (userId, userRole) => {
  try {
    // Native Android/iOS — use Capacitor Push Notifications plugin
    if (Capacitor.isNativePlatform()) {
      const { PushNotifications } = await import('@capacitor/push-notifications');

      // Request permission
      const permResult = await PushNotifications.requestPermissions();
      if (permResult.receive !== 'granted') return null;

      // Register with FCM and get token
      await PushNotifications.register();

      return new Promise((resolve) => {
        PushNotifications.addListener('registration', async (tokenData) => {
          const token = tokenData.value;
          if (token) {
            await saveTokenToFirestore(token, userId, userRole);
          }
          resolve(token);
        });
        PushNotifications.addListener('registrationError', () => resolve(null));
        // Timeout after 10s
        setTimeout(() => resolve(null), 10000);
      });
    }

    // Web / PWA — use Firebase Messaging via Service Worker
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;

    const permission = Notification.permission;
    if (permission !== 'granted') return null;

    const registration = await navigator.serviceWorker.ready;
    const msg = getMessagingInstance();

    const token = await getToken(msg, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      await saveTokenToFirestore(token, userId, userRole);
    }

    return token;
  } catch (error) {
    console.error('🔔 FCM token registration failed:', error);
    return null;
  }
};

/**
 * Listen for FCM messages while the app is in the foreground.
 * Native: uses Capacitor listener. Web: uses Firebase onMessage.
 */
export const setupForegroundMessages = () => {
  try {
    // Native — Capacitor handles foreground notifications natively
    if (Capacitor.isNativePlatform()) {
      import('@capacitor/push-notifications').then(({ PushNotifications }) => {
        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          // Notification is automatically shown by the OS on native
          // No extra handling needed unless you want custom in-app UI
        });
      });
      return () => {}; // return no-op unsubscribe
    }

    // Web / PWA
    const msg = getMessagingInstance();
    return onMessage(msg, (payload) => {
      const title = payload.data?.title || payload.notification?.title || 'PCake';
      const body = payload.data?.body || payload.notification?.body || 'You have new messages';
      const url = payload.data?.url || '/';
      const tag = payload.data?.tag || 'chat-notification';

      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.showNotification(title, {
            body,
            icon: '/pcakelogo-192.png',
            badge: '/pcakelogo.png',
            tag,
            renotify: true,
            data: { url },
          });
        });
      }
    });
  } catch (error) {
    console.error('FCM foreground setup failed:', error);
    return null;
  }
};

